import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

export const usePlaceData = (placeId: string, userId?: string) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedPlace, setEditedPlace] = useState<any>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Query for place data with collection information
  const { data: place, isLoading } = useQuery({
    queryKey: ['place', placeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destination_playlist_items')
        .select(`
          *,
          playlist:destination_playlists!destination_playlist_items_playlist_id_fkey(
            id,
            name,
            city,
            country
          )
        `)
        .eq('id', placeId)
        .single();
      
      if (error) throw error;
      
      // Flatten the playlist data for easier access
      const flattenedData = {
        ...data,
        playlist_name: data.playlist?.name,
        playlist_city: data.playlist?.city,
        playlist_country: data.playlist?.country
      };
      
      return flattenedData;
    },
    enabled: !!placeId
  });

  // Fetch place comments
  const { data: comments } = useQuery({
    queryKey: ['place-comments', placeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('place_comments')
        .select(`
          *,
          user:user_id (
            id,
            username,
            avatar_url
          )
        `)
        .eq('place_id', placeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  // Save mutations
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('Must be logged in to edit');
      if (place?.original_place_id) throw new Error('Cannot edit a copied place');
      
      const { error } = await supabase
        .from('destination_playlist_items')
        .update(editedPlace)
        .eq('id', placeId)
        .eq('created_by_user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place', placeId] });
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Place updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('Must be logged in to delete');
      if (place?.original_place_id) throw new Error('Cannot delete a copied place');
      
      const { error } = await supabase
        .from('destination_playlist_items')
        .delete()
        .eq('id', placeId)
        .eq('created_by_user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Place deleted successfully",
      });
      navigate(-1);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    if (place && !editedPlace) {
      setEditedPlace({ ...place });
    }
  }, [place, editedPlace]);

  const handleInputChange = (field: string, value: any) => {
    setEditedPlace(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return {
    place,
    editedPlace,
    isEditing,
    comments: comments || [],
    setIsEditing,
    setEditedPlace,
    handleInputChange,
    saveMutation,
    deleteMutation,
    isLoading
  };
};
